/**
 * Veridian Ginie v2.0 â€” by Abdolmadjid Masoomi
 * Chat assistant + Admin CLI (superadmin-only terminal).
 * FIXED VERSION - Security, performance, and accessibility improvements.
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent,
} from "react";
import VeridianBadge from "../VeridianBadge";
import { BRAND, PRODUCTS } from "../../branding";

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  error?: boolean;
}

interface TermLine {
  id: string;
  type: "input" | "output" | "error" | "system";
  content: string;
}

type TabId = "chat" | "cli";

// â”€â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const API_BASE = "/api/veridian";
const MAX_RETRIES = 3;
const CLI_TOKEN_KEY = "veridian_cli_token";
const CHAT_HISTORY_KEY = "veridian_chat_history";

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const makeId = () => Math.random().toString(36).slice(2, 9);

const formatTime = (d: Date) =>
  d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

const sanitizeInput = (input: string): string => {
  return input.replace(/[<>]/g, "").trim();
};

const hashPin = async (pin: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + "veridian_salt");
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// â”€â”€â”€ CSS (keyframes injected once) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const injectGlobalCSS = () => {
  if (document.getElementById('veridian-global-css')) return;
  
  const style = document.createElement('style');
  style.id = 'veridian-global-css';
  style.textContent = `
    @keyframes vg-bounce {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
      30% { transform: translateY(-6px); opacity: 1; }
    }
    @keyframes vg-blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0; }
    }
    @keyframes vg-fadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes vg-countdown {
      from { width: 100%; }
      to { width: 0%; }
    }
    @keyframes vg-pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
      50% { box-shadow: 0 0 0 8px rgba(99,102,241,0.15); }
    }
    .vg-msg-enter { animation: vg-fadeIn 0.25s ease forwards; }
    .vg-term-line { animation: vg-fadeIn 0.1s ease forwards; }
    textarea.vg-input:focus { border-color: rgba(99,102,241,0.6) !important; }
    .vg-cli-input:focus { outline: none; background: rgba(99,102,241,0.08); }
    .vg-tab-active { border-bottom: 2px solid #818cf8 !important; color: #818cf8 !important; }
    .vg-suggestion:hover { background: rgba(99,102,241,0.22) !important; border-color: rgba(99,102,241,0.5) !important; }
    
    /* Responsive styles */
    @media (max-width: 768px) {
      .vg-header { padding: 12px 16px 0 !important; }
      .vg-chat-container { padding: 16px !important; }
      .vg-input-area { padding: 10px 16px 14px !important; }
      .vg-suggestions { flex-direction: column !important; align-items: stretch !important; }
    }
  `;
  document.head.appendChild(style);
};

// â”€â”€â”€ API Service â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const apiService = {
  async fetchWithRetry(url: string, options: RequestInit = {}, retries = MAX_RETRIES): Promise<Response> {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "VeridianGinie",
            ...options.headers,
          },
        });
        
        if (response.status >= 500 && i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
          continue;
        }
        
        return response;
      } catch (error) {
        if (i === retries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    throw new Error("Max retries exceeded");
  },

  async warmup(): Promise<void> {
    try {
      await this.fetchWithRetry(`${API_BASE}/warmup`, { method: "POST" });
    } catch (error) {
      console.warn("Warmup failed:", error);
    }
  },

  async sendChatMessage(messages: any[]): Promise<any> {
    const response = await this.fetchWithRetry(`${API_BASE}/chat`, {
      method: "POST",
      body: JSON.stringify({
        messages,
        max_tokens: 1024,
        temperature: 0.7,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  },

  async unlockCLI(pinHash: string): Promise<any> {
    const response = await this.fetchWithRetry(`${API_BASE}/cli/unlock`, {
      method: "POST",
      body: JSON.stringify({ pin_hash: pinHash }),
    });

    if (!response.ok) {
      throw new Error(response.status === 403 ? "Invalid PIN" : "Server error");
    }

    return response.json();
  },

  async executeCLICommand(command: string, token: string): Promise<any> {
    const response = await this.fetchWithRetry(`${API_BASE}/cli/exec`, {
      method: "POST",
      body: JSON.stringify({ command, token }),
    });

    if (response.status === 403) {
      throw new Error("SESSION_EXPIRED");
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Command failed`);
    }

    return response.json();
  },
};

// â”€â”€â”€ Local Storage Service â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const storageService = {
  saveCLIToken(token: string): void {
    try {
      localStorage.setItem(CLI_TOKEN_KEY, token);
    } catch (error) {
      console.warn("Failed to save CLI token:", error);
    }
  },

  getCLIToken(): string | null {
    try {
      return localStorage.getItem(CLI_TOKEN_KEY);
    } catch {
      return null;
    }
  },

  clearCLIToken(): void {
    try {
      localStorage.removeItem(CLI_TOKEN_KEY);
    } catch (error) {
      console.warn("Failed to clear CLI token:", error);
    }
  },

  saveChatHistory(messages: ChatMessage[]): void {
    try {
      const serialized = JSON.stringify(messages.slice(-50)); // Keep last 50 messages
      localStorage.setItem(CHAT_HISTORY_KEY, serialized);
    } catch (error) {
      console.warn("Failed to save chat history:", error);
    }
  },

  loadChatHistory(): ChatMessage[] {
    try {
      const serialized = localStorage.getItem(CHAT_HISTORY_KEY);
      if (!serialized) return [];
      const messages = JSON.parse(serialized);
      return messages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    } catch {
      return [];
    }
  },

  clearChatHistory(): void {
    try {
      localStorage.removeItem(CHAT_HISTORY_KEY);
    } catch (error) {
      console.warn("Failed to clear chat history:", error);
    }
  },
};

// â”€â”€â”€ Subcomponents â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const TypingDots: React.FC = () => (
  <div
    role="status"
    aria-label="AI is typing"
    style={{
      display: "flex",
      gap: 4,
      alignItems: "center",
      padding: "14px 16px",
      background: "rgba(30,30,50,0.8)",
      border: "1px solid rgba(99,102,241,0.2)",
      borderRadius: 16,
      borderBottomLeftRadius: 4,
      width: "fit-content",
    }}
  >
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "#818cf8",
          animation: `vg-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
        }}
      />
    ))}
  </div>
);

const Cursor: React.FC = () => (
  <span
    style={{
      display: "inline-block",
      width: 8,
      height: 14,
      background: "#4ade80",
      marginLeft: 2,
      verticalAlign: "middle",
      animation: "vg-blink 1s step-end infinite",
    }}
  />
);

// â”€â”€â”€ Cold-Start Retry Banner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface RetryBannerProps {
  seconds: number;
  onRetry: () => void;
}

const RetryBanner: React.FC<RetryBannerProps> = ({ seconds, onRetry }) => (
  <div
    role="alert"
    aria-live="polite"
    style={{
      marginBottom: 10,
      borderRadius: 10,
      overflow: "hidden",
      border: "1px solid rgba(245,158,11,0.35)",
      background: "rgba(245,158,11,0.08)",
    }}
  >
    <div
      style={{
        padding: "8px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      }}
    >
      <span style={{ color: "#fbbf24", fontSize: 12 }}>
        â³ Model is waking up â€” auto-retrying in{" "}
        <strong>{seconds}s</strong>
      </span>
      <button
        onClick={onRetry}
        aria-label="Retry now"
        style={{
          background: "rgba(245,158,11,0.2)",
          border: "1px solid rgba(245,158,11,0.4)",
          borderRadius: 6,
          padding: "3px 12px",
          color: "#fbbf24",
          fontSize: 11,
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Retry Now
      </button>
    </div>
    {/* countdown bar */}
    <div style={{ height: 3, background: "rgba(245,158,11,0.15)" }}>
      <div
        style={{
          height: "100%",
          background: "#fbbf24",
          animation: `vg-countdown ${seconds}s linear forwards`,
        }}
      />
    </div>
  </div>
);

// â”€â”€â”€ Chat Suggestions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const SUGGESTIONS = [
  "Help me write a sales email",
  `SEO title for ${PRODUCTS.LIZZE} flat iron`,
  `Pricing strategy for ${PRODUCTS.ENZO} tools`,
  "Draft a WhatsApp follow-up",
  "Analyze competitor pricing",
];

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const VeridianGinie: React.FC = () => {
  // Inject CSS on mount
  useEffect(() => {
    injectGlobalCSS();
  }, []);

  // â”€â”€ Tab state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [activeTab, setActiveTab] = useState<TabId>("chat");

  // â”€â”€ Chat state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [messages, setMessages] = useState<ChatMessage[]>(() => 
    storageService.loadChatHistory()
  );
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState<number | null>(null);
  const [pendingRetryMsg, setPendingRetryMsg] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // â”€â”€ CLI state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [cliUnlocked, setCliUnlocked] = useState(() => !!storageService.getCLIToken());
  const [cliToken, setCliToken] = useState(() => storageService.getCLIToken() || "");
  const [cliPinInput, setCliPinInput] = useState("");
  const [cliPinError, setCliPinError] = useState("");
  const [cliPinLoading, setCliPinLoading] = useState(false);
  const [termLines, setTermLines] = useState<TermLine[]>([
    {
      id: makeId(),
      type: "system",
      content:
        "Veridian Ginie Admin CLI v2.0\n" +
        "Superadmin: Abdolmadjid Masoomi\n" +
        "â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€\n" +
        "Enter PIN to unlock.",
    },
  ]);
  const [cliInput, setCliInput] = useState("");
  const [cliLoading, setCliLoading] = useState(false);
  const [cmdHistory, setCmdHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const cliInputRef = useRef<HTMLInputElement>(null);
  const termEndRef = useRef<HTMLDivElement>(null);

  // â”€â”€ Warmup on mount â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    apiService.warmup();
  }, []);

  // â”€â”€ Auto-scroll â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatLoading]);

  useEffect(() => {
    termEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [termLines]);

  // â”€â”€ Textarea auto-resize â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const ta = chatTextareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
    }
  }, [chatInput]);

  // â”€â”€ Save chat history when messages change â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    storageService.saveChatHistory(messages);
  }, [messages]);

  // â”€â”€ Retry countdown â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const startRetryCountdown = useCallback(
    (msg: string, delaySeconds: number = 20) => {
      setPendingRetryMsg(msg);
      setRetryCountdown(delaySeconds);

      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);

      retryTimerRef.current = setTimeout(() => {
        if (msg) {
          setMessages((prev) => prev.filter((m) => !m.error));
          sendChatMessage(msg, true);
        }
        setPendingRetryMsg(null);
        setRetryCountdown(null);
      }, delaySeconds * 1000);
    },
    []
  );

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // â”€â”€ Send chat message â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const sendChatMessage = useCallback(
    async (text: string, isAutoRetry: boolean = false) => {
      if (!text.trim() || chatLoading) return;

      if (!isAutoRetry) {
        const userMsg: ChatMessage = {
          id: makeId(),
          role: "user",
          content: sanitizeInput(text),
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMsg]);
        setChatInput("");
        setRetryCountdown(null);
        setPendingRetryMsg(null);
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      }

      setChatLoading(true);

      const history = messages.slice(-9).concat(
        isAutoRetry
          ? []
          : [{ id: "", role: "user" as const, content: sanitizeInput(text), timestamp: new Date() }]
      );

      try {
        const data = await apiService.sendChatMessage([
          {
            role: "system",
            content:
              `You are Veridian Ginie, an intelligent AI assistant created by Abdolmadjid Masoomi for ${BRAND.company} â€” a Dubai beauty tools distributor. ` +
              `You assist with business tasks: writing, SEO, sales strategy, product descriptions, pricing, and operational analysis. ` +
              `Be concise, professional, and commercially sharp. Know that ${PRODUCTS.ENZO} and ${PRODUCTS.LIZZE} are the company's exclusive brands.`,
          },
          ...history.map((m) => ({ role: m.role, content: m.content })),
        ]);

        setMessages((prev) => [
          ...prev,
          {
            id: makeId(),
            role: "assistant",
            content: data.reply || "(empty response)",
            timestamp: new Date(),
          },
        ]);
      } catch (err: any) {
        const errorMessage = err.message || "Unknown error";
        
        // Handle specific error types
        if (errorMessage.includes("503") || errorMessage.includes("504")) {
          setMessages((prev) => [
            ...prev,
            {
              id: makeId(),
              role: "assistant",
              content: "Model is warming up â€” retrying automatically...",
              timestamp: new Date(),
              error: true,
            },
          ]);
          startRetryCountdown(text.trim(), 20);
          return;
        }

        if (errorMessage.includes("401") || errorMessage.includes("403")) {
          setMessages((prev) => [
            ...prev,
            {
              id: makeId(),
              role: "assistant",
              content: "Session expired. Please refresh the page.",
              timestamp: new Date(),
              error: true,
            },
          ]);
          return;
        }

        setMessages((prev) => [
          ...prev,
          {
            id: makeId(),
            role: "assistant",
            content: `Error: ${errorMessage}. Please try again.`,
            timestamp: new Date(),
            error: true,
          },
        ]);
      } finally {
        setChatLoading(false);
      }
    },
    [messages, chatLoading, startRetryCountdown]
  );

  const handleChatKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage(chatInput);
    }
  };

  // â”€â”€ CLI unlock â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleCLIUnlock = async () => {
    if (!cliPinInput.trim()) return;
    setCliPinLoading(true);
    setCliPinError("");

    try {
      const pinHash = await hashPin(cliPinInput);
      const data = await apiService.unlockCLI(pinHash);
      
      setCliToken(data.token);
      setCliUnlocked(true);
      setCliPinInput("");
      storageService.saveCLIToken(data.token);
      
      setTermLines([
        {
          id: makeId(),
          type: "system",
          content:
            "â•”â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•—\n" +
            "â•‘  VERIDIAN GINIE â€” ADMIN CLI v2.0     â•‘\n" +
            "â•‘  Superadmin: Abdolmadjid Masoomi     â•‘\n" +
            "â•‘  Session valid: 4 hours              â•‘\n" +
            "â•šâ•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•\n\n" +
            "Type /help for available commands.",
        },
      ]);
      setTimeout(() => cliInputRef.current?.focus(), 100);
    } catch (error: any) {
      setCliPinError(error.message || "Connection error â€” check if backend is running.");
    } finally {
      setCliPinLoading(false);
    }
  };

  // â”€â”€ CLI exec â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleCLIExec = async () => {
    const cmd = cliInput.trim();
    if (!cmd || cliLoading) return;

    // Update history
    setCmdHistory((prev) => [cmd, ...prev.slice(0, 49)]);
    setHistoryIdx(-1);
    setCliInput("");

    // Print the input line
    setTermLines((prev) => [
      ...prev,
      { id: makeId(), type: "input", content: `> ${cmd}` },
    ]);

    if (cmd === "clear" || cmd === "/clear" || cmd === "cls" || cmd === "/cls") {
      setTermLines([
        { id: makeId(), type: "system", content: "Terminal cleared." },
      ]);
      return;
    }

    if (!cliToken) {
      setTermLines((prev) => [
        ...prev,
        {
          id: makeId(),
          type: "error",
          content: "No valid session â€” please unlock CLI first.",
        },
      ]);
      setCliUnlocked(false);
      return;
    }

    setCliLoading(true);

    try {
      const data = await apiService.executeCLICommand(cmd, cliToken);
      const output: string = data.output || "(no output)";

      if (output === "__CLEAR__") {
        setTermLines([
          { id: makeId(), type: "system", content: "Terminal cleared." },
        ]);
      } else {
        setTermLines((prev) => [
          ...prev,
          { id: makeId(), type: "output", content: output },
        ]);
      }
    } catch (err: any) {
      const errorMessage = err.message || "Unknown error";
      
      if (errorMessage === "SESSION_EXPIRED") {
        setCliUnlocked(false);
        setCliToken("");
        storageService.clearCLIToken();
        setTermLines((prev) => [
          ...prev,
          {
            id: makeId(),
            type: "error",
            content: "Session expired â€” please re-enter your PIN.",
          },
        ]);
        return;
      }

      setTermLines((prev) => [
        ...prev,
        {
          id: makeId(),
          type: "error",
          content: `Error: ${errorMessage}`,
        },
      ]);
    } finally {
      setCliLoading(false);
    }
  };

  const handleCLIKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCLIExec();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const newIdx = Math.min(historyIdx + 1, cmdHistory.length - 1);
      setHistoryIdx(newIdx);
      setCliInput(cmdHistory[newIdx] || "");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const newIdx = Math.max(historyIdx - 1, -1);
      setHistoryIdx(newIdx);
      setCliInput(newIdx === -1 ? "" : cmdHistory[newIdx] || "");
    }
  };

  const handleLockCLI = () => {
    setCliUnlocked(false);
    setCliToken("");
    storageService.clearCLIToken();
    setTermLines([
      {
        id: makeId(),
        type: "system",
        content: "Session locked. Enter PIN to re-unlock.",
      },
    ]);
  };

  const canSendChat = chatInput.trim().length > 0 && !chatLoading;

  // â”€â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #0f0f1a 100%)",
        color: "#e2e8f0",
        fontFamily: "'Inter', -apple-system, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
      role="main"
      aria-label="Veridian Ginie AI Assistant"
    >
      {/* Ambient glows */}
      <div
        style={{
          position: "absolute", top: "-20%", left: "-10%",
          width: 600, height: 600,
          background: "radial-gradient(circle, rgba(99,102,241,0.13) 0%, transparent 70%)",
          pointerEvents: "none", zIndex: 0,
        }}
      />
      <div
        style={{
          position: "absolute", bottom: "-20%", right: "-10%",
          width: 500, height: 500,
          background: "radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)",
          pointerEvents: "none", zIndex: 0,
        }}
      />

      {/* â”€â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <header
        className="vg-header"
        style={{
          position: "relative", zIndex: 10,
          padding: "16px 24px 0",
          borderBottom: "1px solid rgba(99,102,241,0.15)",
          background: "rgba(15,15,26,0.85)",
          backdropFilter: "blur(12px)",
        }}
      >
        {/* Top row: logo + title + badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
                boxShadow: "0 0 18px rgba(99,102,241,0.4)",
                animation: "vg-pulse 3s ease-in-out infinite",
              }}
              aria-hidden="true"
            >
              <img
                src="/brand/veridian-logo.png"
                alt="Veridian Logo"
                style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 16, fontWeight: 700,
                  background: "linear-gradient(90deg, #818cf8, #c084fc)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  lineHeight: 1.2,
                }}
              >
                Veridian Ginie v2.0
              </div>
              <div style={{ fontSize: 11, color: "rgba(148,163,184,0.7)", marginTop: 1 }}>
                by Abdolmadjid Â· INDEPENDENT MODULE
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <VeridianBadge
              variant="pill"
              label="Veridian Integration v1"
              tooltip="asyncpg pool + OpenRouter LLM + module access + Stripe billing + campaign engine"
            />
            <div
              style={{
                padding: "3px 10px", borderRadius: 20,
                background: "rgba(99,102,241,0.12)",
                border: "1px solid rgba(99,102,241,0.25)",
                fontSize: 11, color: "#818cf8",
                display: "flex", alignItems: "center", gap: 5,
              }}
              role="status"
              aria-label="Connection status: Online"
            >
              <div
                style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "#6ee7b7",
                  boxShadow: "0 0 5px rgba(110,231,183,0.8)",
                }}
              />
              Online
            </div>
            {activeTab === "chat" && messages.length > 0 && (
              <button
                onClick={() => {
                  setMessages([]);
                  storageService.clearChatHistory();
                }}
                aria-label="Clear chat history"
                style={{
                  padding: "4px 12px", borderRadius: 7,
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  color: "#fca5a5", fontSize: 11, cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <nav style={{ display: "flex", gap: 0 }} aria-label="Main navigation">
          {(["chat", "cli"] as TabId[]).map((tab) => (
            <button
              key={tab}
              className={activeTab === tab ? "vg-tab-active" : ""}
              onClick={() => setActiveTab(tab)}
              aria-selected={activeTab === tab}
              aria-controls={`panel-${tab}`}
              role="tab"
              style={{
                padding: "8px 20px",
                background: "transparent",
                border: "none",
                borderBottom: "2px solid transparent",
                color: activeTab === tab ? "#818cf8" : "rgba(148,163,184,0.55)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: "0.04em",
                textTransform: "uppercase" as const,
                transition: "color 0.2s",
              }}
            >
              {tab === "cli" ? "âš¡ Admin CLI" : "ðŸ’¬ Chat"}
            </button>
          ))}
        </nav>
      </header>

      {/* â”€â”€â”€ Chat Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === "chat" && (
        <div
          id="panel-chat"
          role="tabpanel"
          aria-labelledby="tab-chat"
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          {messages.length === 0 ? (
            <div
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 18, padding: 40, textAlign: "center",
                position: "relative", zIndex: 1,
              }}
            >
              <div style={{ fontSize: 50, filter: "drop-shadow(0 0 18px rgba(99,102,241,0.5))" }}>
                ðŸ§ž
              </div>
              <div
                style={{
                  fontSize: 22, fontWeight: 700,
                  background: "linear-gradient(90deg, #818cf8, #c084fc)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}
              >
                Veridian Ginie v2.0
              </div>
              <div
                style={{
                  fontSize: 13, color: "rgba(148,163,184,0.65)",
                  maxWidth: 360, lineHeight: 1.7,
                }}
              >
                Your intelligent assistant by Abdolmadjid. Ask anything â€” sales strategy,
                copywriting, SEO, product descriptions, or analysis.
              </div>
              <div className="vg-suggestions" style={{ display: "flex", flexWrap: "wrap" as const, justifyContent: "center", gap: 8, marginTop: 4 }}>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    className="vg-suggestion"
                    onClick={() => sendChatMessage(s)}
                    aria-label={`Suggestion: ${s}`}
                    style={{
                      padding: "7px 14px", borderRadius: 20,
                      background: "rgba(99,102,241,0.09)",
                      border: "1px solid rgba(99,102,241,0.22)",
                      color: "#a5b4fc", fontSize: 12,
                      cursor: "pointer", transition: "all 0.2s",
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div
              className="vg-chat-container"
              style={{
                flex: 1, overflowY: "auto",
                padding: "20px 24px",
                display: "flex", flexDirection: "column",
                gap: 14, position: "relative", zIndex: 1,
              }}
              role="log"
              aria-label="Chat messages"
              aria-live="polite"
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="vg-msg-enter"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    maxWidth: "80%",
                    alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                    alignItems: msg.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      padding: "11px 15px",
                      borderRadius: 16,
                      lineHeight: 1.65,
                      fontSize: 14,
                      wordBreak: "break-word" as const,
                      ...(msg.role === "user"
                        ? {
                            background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                            color: "#fff",
                            borderBottomRightRadius: 4,
                            boxShadow: "0 4px 10px rgba(79,70,229,0.3)",
                          }
                        : msg.error
                        ? {
                            background: "rgba(245,158,11,0.1)",
                            border: "1px solid rgba(245,158,11,0.3)",
                            color: "#fbbf24",
                            borderBottomLeftRadius: 4,
                          }
                        : {
                            background: "rgba(26,26,48,0.85)",
                            border: "1px solid rgba(99,102,241,0.18)",
                            color: "#e2e8f0",
                            borderBottomLeftRadius: 4,
                            whiteSpace: "pre-wrap" as const,
                          }),
                    }}
                    role={msg.error ? "alert" : undefined}
                  >
                    {msg.content}
                  </div>
                  <span style={{ fontSize: 10, color: "rgba(148,163,184,0.35)", padding: "0 3px" }}>
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
              ))}
              {chatLoading && <TypingDots />}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Chat input area */}
          <div
            className="vg-input-area"
            style={{
              position: "relative", zIndex: 10,
              padding: "12px 24px 16px",
              borderTop: "1px solid rgba(99,102,241,0.12)",
              background: "rgba(15,15,26,0.92)",
              backdropFilter: "blur(12px)",
            }}
          >
            {retryCountdown !== null && pendingRetryMsg && (
              <RetryBanner
                seconds={retryCountdown}
                onRetry={() => {
                  if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                  setRetryCountdown(null);
                  setMessages((prev) => prev.filter((m) => !m.error));
                  sendChatMessage(pendingRetryMsg, true);
                  setPendingRetryMsg(null);
                }}
              />
            )}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <textarea
                ref={chatTextareaRef}
                className="vg-input"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKey}
                placeholder="Message Veridian Ginieâ€¦"
                rows={1}
                disabled={chatLoading}
                aria-label="Chat input"
                style={{
                  flex: 1,
                  background: "rgba(26,26,48,0.7)",
                  border: "1px solid rgba(99,102,241,0.2)",
                  borderRadius: 12,
                  padding: "11px 15px",
                  color: "#e2e8f0",
                  fontSize: 14,
                  resize: "none" as const,
                  outline: "none",
                  lineHeight: 1.5,
                  minHeight: 46,
                  maxHeight: 160,
                  transition: "border-color 0.2s",
                  fontFamily: "inherit",
                }}
              />
              <button
                onClick={() => sendChatMessage(chatInput)}
                disabled={!canSendChat}
                aria-label="Send message"
                style={{
                  width: 46, height: 46, borderRadius: 12, flexShrink: 0,
                  background: canSendChat
                    ? "linear-gradient(135deg, #4f46e5, #7c3aed)"
                    : "rgba(99,102,241,0.15)",
                  border: "none",
                  cursor: canSendChat ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 17,
                  boxShadow: canSendChat ? "0 4px 10px rgba(79,70,229,0.3)" : "none",
                  transition: "all 0.2s",
                }}
              >
                âž¤
              </button>
            </div>
            <div
              style={{
                fontSize: 10, color: "rgba(148,163,184,0.35)",
                marginTop: 6, textAlign: "center" as const,
              }}
            >
              Enter to send Â· Shift+Enter for new line
            </div>
          </div>
        </div>
      )}

      {/* â”€â”€â”€ CLI Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === "cli" && (
        <div
          id="panel-cli"
          role="tabpanel"
          aria-labelledby="tab-cli"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          {!cliUnlocked ? (
            /* â”€â”€ PIN unlock screen â”€â”€ */
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 40,
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 380,
                  background: "rgba(15,15,26,0.9)",
                  border: "1px solid rgba(99,102,241,0.25)",
                  borderRadius: 16,
                  padding: "36px 32px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cli-unlock-title"
              >
                <div style={{ textAlign: "center" as const, marginBottom: 28 }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>ðŸ”</div>
                  <div
                    id="cli-unlock-title"
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      background: "linear-gradient(90deg, #818cf8, #c084fc)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Admin CLI Access
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "rgba(148,163,184,0.6)",
                      marginTop: 6,
                    }}
                  >
                    Restricted to Abdolmadjid Masoomi
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label
                    htmlFor="cli-pin-input"
                    style={{
                      fontSize: 11,
                      color: "rgba(148,163,184,0.7)",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase" as const,
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    Admin PIN
                  </label>
                  <input
                    id="cli-pin-input"
                    type="password"
                    value={cliPinInput}
                    onChange={(e) => {
                      setCliPinInput(e.target.value);
                      setCliPinError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCLIUnlock();
                    }}
                    placeholder="Enter PINâ€¦"
                    autoFocus
                    aria-describedby={cliPinError ? "cli-pin-error" : undefined}
                    style={{
                      width: "100%",
                      background: "rgba(30,30,50,0.7)",
                      border: `1px solid ${cliPinError ? "rgba(239,68,68,0.5)" : "rgba(99,102,241,0.3)"}`,
                      borderRadius: 10,
                      padding: "11px 14px",
                      color: "#e2e8f0",
                      fontSize: 16,
                      outline: "none",
                      fontFamily: "monospace",
                      letterSpacing: "0.2em",
                      boxSizing: "border-box" as const,
                      transition: "border-color 0.2s",
                    }}
                  />
                  {cliPinError && (
                    <div
                      id="cli-pin-error"
                      role="alert"
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: "#fca5a5",
                      }}
                    >
                      {cliPinError}
                    </div>
                  )}
                </div>

                <button
                  onClick={handleCLIUnlock}
                  disabled={cliPinLoading || !cliPinInput.trim()}
                  aria-label="Unlock CLI"
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: 10,
                    background:
                      cliPinLoading || !cliPinInput.trim()
                        ? "rgba(99,102,241,0.2)"
                        : "linear-gradient(135deg, #4f46e5, #7c3aed)",
                    border: "none",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor:
                      cliPinLoading || !cliPinInput.trim()
                        ? "not-allowed"
                        : "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {cliPinLoading ? "Verifyingâ€¦" : "Unlock CLI"}
                </button>
              </div>
            </div>
          ) : (
            /* â”€â”€ Terminal â”€â”€ */
            <>
              {/* Terminal output */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "20px 24px",
                  fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
                  fontSize: 13,
                  lineHeight: 1.7,
                  background: "rgba(8,8,18,0.7)",
                }}
                onClick={() => cliInputRef.current?.focus()}
                role="log"
                aria-label="CLI output"
                aria-live="polite"
              >
                {termLines.map((line) => (
                  <div
                    key={line.id}
                    className="vg-term-line"
                    style={{
                      marginBottom: 4,
                      whiteSpace: "pre-wrap" as const,
                      color:
                        line.type === "input"
                          ? "#c084fc"
                          : line.type === "error"
                          ? "#f87171"
                          : line.type === "system"
                          ? "#818cf8"
                          : "#4ade80",
                      borderLeft:
                        line.type === "input"
                          ? "2px solid rgba(192,132,252,0.3)"
                          : line.type === "error"
                          ? "2px solid rgba(248,113,113,0.3)"
                          : "none",
                      paddingLeft: line.type === "input" || line.type === "error" ? 10 : 0,
                    }}
                    role={line.type === "error" ? "alert" : undefined}
                  >
                    {line.content}
                  </div>
                ))}
                {cliLoading && (
                  <div style={{ color: "#818cf8", fontSize: 12 }}>
                    Processing<span style={{ animation: "vg-blink 1s step-end infinite" }}>_</span>
                  </div>
                )}
                <div ref={termEndRef} />
              </div>

              {/* Terminal input row */}
              <div
                style={{
                  padding: "12px 24px 16px",
                  borderTop: "1px solid rgba(99,102,241,0.15)",
                  background: "rgba(8,8,18,0.92)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
                }}
              >
                <span style={{ color: "#4ade80", fontSize: 14, flexShrink: 0 }} aria-hidden="true">
                  abdolmadjid@afaq:~$
                </span>
                <input
                  ref={cliInputRef}
                  className="vg-cli-input"
                  type="text"
                  value={cliInput}
                  onChange={(e) => setCliInput(sanitizeInput(e.target.value))}
                  onKeyDown={handleCLIKey}
                  disabled={cliLoading}
                  placeholder="type a commandâ€¦ (â†‘â†“ history)"
                  autoFocus
                  aria-label="CLI command input"
                  style={{
                    flex: 1,
                    background: "transparent",
                    border: "none",
                    color: "#e2e8f0",
                    fontSize: 13,
                    fontFamily: "inherit",
                    outline: "none",
                    padding: "4px 8px",
                    borderRadius: 6,
                    transition: "background 0.15s",
                  }}
                />
                {!cliLoading && <Cursor />}
                <button
                  onClick={handleLockCLI}
                  title="Lock CLI"
                  aria-label="Lock CLI session"
                  style={{
                    background: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    borderRadius: 6,
                    padding: "3px 10px",
                    color: "#fca5a5",
                    fontSize: 11,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  ðŸ”’ Lock
                </button>
              </div>
              <div
                style={{
                  paddingBottom: 6,
                  textAlign: "center" as const,
                  fontSize: 10,
                  color: "rgba(148,163,184,0.3)",
                  background: "rgba(8,8,18,0.92)",
                  fontFamily: "monospace",
                }}
              >
                Enter to run Â· â†‘â†“ history Â· /help for commands Â· /clear to reset
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VeridianGinie;
